import { useSuspenseQuery } from '@apollo/client';
import { UserRoundSearch } from 'lucide-react';
import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet } from 'react-router-dom';

import { Box, LinkButton, LoadingIndicator } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';
import { TabNav } from '@eluve/smart-blocks';

import {
  appointmentTagOptionsFragment,
  reviewerLowQualityFactorsFragment,
  reviewerPromptTemplatesFragment,
  reviewerTenantsFragment,
  reviewerUsersFragment,
} from './operations';

export interface ReviewLayoutProps {}

const persistentQueryParams = ['tenantId', 'userId'];

const reviewerRoutes = [
  {
    name: 'Appointments',
    link: 'appointments',
    persistentQueryParams,
  },
  {
    name: 'Feedback',
    link: 'feedback',
    persistentQueryParams,
  },
];

const adminReviewerRoutes = [
  ...reviewerRoutes,
  {
    name: 'Admin Export',
    link: 'admin/export',
    persistentQueryParams,
  },
];

const baseReviewerDataQuery = graphql(
  `
    query getBaseReviewerData {
      ...ReviewerTenants
      ...ReviewerUsers
      ...ReviewerPromptTemplates
      ...ReviewerLowQualityFactors
      ...TagOptions
    }
  `,
  [
    reviewerTenantsFragment,
    reviewerUsersFragment,
    reviewerLowQualityFactorsFragment,
    reviewerPromptTemplatesFragment,
    appointmentTagOptionsFragment,
  ],
);

export const ReviewLayout: React.FC<ReviewLayoutProps> = () => {
  useSuspenseQuery(baseReviewerDataQuery);
  const isEluveAdmin = useIsEluveAdmin();
  const routes = isEluveAdmin ? adminReviewerRoutes : reviewerRoutes;
  return (
    <>
      <Helmet>
        <title>Review | Eluve</title>
      </Helmet>
      <div className="min-h-full overflow-x-hidden">
        <div className="bg-text-primary pb-32">
          <header className="py-4 lg:py-8">
            <div className="mx-auto w-full px-2 lg:px-8">
              <div className="flex flex-wrap items-center justify-between gap-y-2">
                <div className="flex items-center gap-2">
                  <UserRoundSearch className="size-10 text-border-yellow-subtle" />
                  <h1 className="text-3xl font-bold tracking-tight text-white">
                    Eluve Review Dashboard
                  </h1>
                </div>
                <div className="flex flex-wrap gap-1 md:gap-2">
                  <LinkButton
                    icon={{ name: 'LogOut' }}
                    spacing="compact"
                    text="Return to App"
                    to="/"
                    type="subtle"
                  />
                </div>
              </div>
              <Box className="mt-4">
                <TabNav tabs={routes} colorMode="dark" />
              </Box>
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="mx-auto w-full px-2 pb-12 lg:px-8">
            <div className="min-h-96 rounded-lg bg-white px-5 py-6 shadow sm:px-6">
              <div className="w-full">
                <Suspense fallback={<LoadingIndicator />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </div>
        </main>
        <div id="portal-footer" />
      </div>
    </>
  );
};
